import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSite } from './context';
import { Busy, Failure, Notice } from './bits';
import { Identify } from './Identify';
import { addDays, dayRange, duration, money } from './time';
import { asFailure } from '../api/errors';
import type { Availability, Booking as BookingRecord, OfferedSlot, SiteService } from '../wire';

/**
 * Browse → pick a day → pick a slot → prove who you are → confirm.
 *
 * Four rules hold this flow together, and each is somebody else's decision
 * arriving on a screen:
 *
 * 1. **The price on this page is the catalog's** (`/api/site`), and the request
 *    that books never carries one. `CreateBooking` in `wire.ts` has no field for
 *    money — obligation B7, generalized to the guest surface. There is a test
 *    that reads the outgoing POST body and fails if a price appears in it.
 * 2. **Times are the tenant's wall clock**, formatted through `clock` from
 *    `/api/site`'s timezone. A guest books the spa's two o'clock.
 * 3. **A day with nothing free still answers.** The engine returns
 *    `alternatives` whenever the ask does not fit, and this screen shows them as
 *    the nearby days they are. "No times" with a populated `alternatives` array
 *    sitting unread in the response would be this surface's version of the one
 *    failure the agreement says matters most.
 * 4. **Identity is proved once, as late as possible.** Browsing needs no
 *    session; the OTP step appears between choosing a slot and confirming it,
 *    which is where a guest already understands why they are being asked.
 *
 * ## Why availability is asked one day at a time
 *
 * `GET /api/availability` takes a range, and asking for a fortnight would be one
 * request instead of several. It would also be *worse*: the engine caps the
 * answer at `limit` slots across the whole window, so a busy spa's fortnight
 * comes back as Monday morning and nothing else, and — the part that matters —
 * `alternatives` is computed against the ask. A one-day ask that fails produces
 * the proximity ladder around *that day*, which is exactly the "try Thursday
 * instead" a guest wants. A fortnight-wide ask that partially fills produces
 * nothing so useful.
 */

type Step =
  | { kind: 'pick' }
  | { kind: 'identify'; choice: Choice }
  | { kind: 'confirm'; choice: Choice; intentId: string }
  | { kind: 'done'; booking: BookingRecord };

interface Choice {
  service: SiteService;
  slot: OfferedSlot;
}

const DAYS_SHOWN = 7;

export function BookingFlow(): ReactNode {
  const { site, services, signedIn, clock } = useSite();
  const [step, setStep] = useState<Step>({ kind: 'pick' });

  if (!site.bookable) {
    // `status ∈ {live, importing}` is what `bookable` means. Anything else is a
    // tenant that is onboarding, paused or offboarded, and the honest sentence
    // is that this site is not taking bookings — not a booking form that will
    // answer `503` after the guest has typed their number in.
    return (
      <Notice tone="warn" title="Online booking is not open here yet.">
        <p className="notice__hint">Please get in touch with {site.name} directly.</p>
      </Notice>
    );
  }

  if (services.length === 0) {
    // `bookable_online` is off for everything, or the catalog is empty. Both are
    // the tenant's own configuration and neither is an error.
    return <Notice tone="warn" title="Nothing is bookable online at the moment." />;
  }

  if (step.kind === 'done') {
    const b = step.booking;
    return (
      <div className="booked">
        <Notice title="You are booked.">
          <p>
            {b.service_name} — {clock.slot(b.start, b.end)}
          </p>
        </Notice>
        <p className="booked__ref">Reference {b.booking_id}</p>
        <button
          className="button button--quiet"
          type="button"
          onClick={() => setStep({ kind: 'pick' })}
        >
          Book something else
        </button>
      </div>
    );
  }

  if (step.kind === 'identify') {
    return (
      <div className="flow">
        <ChosenSlot choice={step.choice} onChange={() => setStep({ kind: 'pick' })} />
        <Identify
          purpose="Almost done — we just need to know who the booking is for."
          onSignedIn={() => setStep({ kind: 'confirm', choice: step.choice, intentId: intentId() })}
        />
      </div>
    );
  }

  if (step.kind === 'confirm') {
    return (
      <Confirm
        choice={step.choice}
        intentId={step.intentId}
        onBooked={(booking) => setStep({ kind: 'done', booking })}
        onChange={() => setStep({ kind: 'pick' })}
        onSessionEnded={() => setStep({ kind: 'identify', choice: step.choice })}
      />
    );
  }

  return (
    <Picker
      onPicked={(choice) =>
        setStep(
          signedIn
            ? { kind: 'confirm', choice, intentId: intentId() }
            : { kind: 'identify', choice },
        )
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Step 1 — the service, the day, the slot                             */
/* ------------------------------------------------------------------ */

function Picker({ onPicked }: { onPicked: (choice: Choice) => void }): ReactNode {
  const { api, site, services, clock } = useSite();
  const first = services[0];
  const [serviceId, setServiceId] = useState(first === undefined ? '' : first.service_id);
  const [day, setDay] = useState(() => clock.today());
  const [answer, setAnswer] = useState<Availability | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const service = useMemo(
    () => services.find((s) => s.service_id === serviceId),
    [services, serviceId],
  );

  const load = useCallback(
    (signal: AbortSignal) => {
      setBusy(true);
      setError(null);
      api
        .availability({ service_id: serviceId, date_from: day, date_to: day, limit: 50 }, signal)
        .then((result) => {
          if (!signal.aborted) setAnswer(result);
        })
        .catch((e: unknown) => {
          if (!signal.aborted) setError(e);
        })
        .finally(() => {
          if (!signal.aborted) setBusy(false);
        });
    },
    [api, serviceId, day],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const days = useMemo(() => dayRange(clock.today(), DAYS_SHOWN), [clock]);
  const nearby = useMemo(
    () => groupByDay(answer?.alternatives ?? [], clock.dayOf),
    [answer, clock],
  );

  return (
    <div className="picker">
      {services.length > 1 ? (
        <fieldset className="picker__services">
          <legend className="picker__legend">Choose a service</legend>
          {services.map((s) => (
            <button
              key={s.service_id}
              type="button"
              className={`chip chip--service ${s.service_id === serviceId ? 'chip--on' : ''}`}
              aria-pressed={s.service_id === serviceId}
              onClick={() => setServiceId(s.service_id)}
            >
              <span className="chip__name">{s.name}</span>
              <span className="chip__meta">
                {duration(s.duration_min)} · {money(s.price_cents, site.currency)}
              </span>
            </button>
          ))}
        </fieldset>
      ) : null}

      <fieldset className="picker__days">
        <legend className="picker__legend">Choose a day</legend>
        <div className="picker__dayrow">
          {days.map((d) => (
            <button
              key={d}
              type="button"
              className={`chip chip--day ${d === day ? 'chip--on' : ''}`}
              aria-pressed={d === day}
              onClick={() => setDay(d)}
            >
              {clock.dayShort(d)}
            </button>
          ))}
          <button
            className="button button--quiet"
            type="button"
            onClick={() => setDay(addDays(day, DAYS_SHOWN))}
          >
            Later →
          </button>
        </div>
      </fieldset>

      {busy ? <Busy label="Looking for times…" /> : null}
      {error === null ? null : <Failure error={error} />}

      {answer === null || busy ? null : (
        <div
          className="picker__slots"
          role="region"
          aria-live="polite"
          aria-label="Available times"
        >
          {/* The engine's own sentence. It is deterministic, it already says
              what the window was and what was found, and re-deriving it here
              would be a second summary that disagrees with the one the
              concierge reads out loud. */}
          <p className="picker__summary">{answer.summary}</p>

          {answer.authority === 'approximate' ? (
            <Notice tone="warn" title="These times are a guide.">
              <p className="notice__hint">
                {site.name} is still moving their diary over, so we will confirm with them.
              </p>
            </Notice>
          ) : null}

          {answer.slots.length > 0 ? (
            <ul className="slots">
              {answer.slots.map((slot) => (
                <li key={`${slot.start}|${slot.resource_id}`}>
                  <button
                    className="button button--slot"
                    type="button"
                    onClick={() => {
                      if (service !== undefined) onPicked({ service, slot });
                    }}
                  >
                    {clock.time(slot.start)}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="slots__empty">Nothing free that day.</p>
          )}

          {answer.slots.length === 0 && nearby.length > 0 ? (
            <div className="nearby">
              <h4 className="nearby__title">Nearby days with space</h4>
              <div className="picker__dayrow">
                {nearby.map(([d, slots]) => (
                  <button
                    key={d}
                    type="button"
                    className="chip chip--day"
                    onClick={() => setDay(d)}
                  >
                    {clock.dayShort(d)}
                    <span className="chip__meta">
                      {slots.length === 1 ? '1 time' : `${String(slots.length)} times`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Alternatives, gathered into the days they fall on **in the tenant's zone**.
 *
 * Grouping on the raw ISO prefix would be grouping by UTC day, which in St
 * Thomas moves every evening booking after 20:00 onto tomorrow's chip. So the
 * key comes from the clock, like every other date on this page.
 */
function groupByDay(
  slots: readonly OfferedSlot[],
  dayOf: (iso: string) => string,
): [string, OfferedSlot[]][] {
  const byDay = new Map<string, OfferedSlot[]>();
  for (const slot of slots) {
    const day = dayOf(slot.start);
    const list = byDay.get(day);
    if (list === undefined) byDay.set(day, [slot]);
    else list.push(slot);
  }
  return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/* ------------------------------------------------------------------ */
/* Step 3 — confirm                                                    */
/* ------------------------------------------------------------------ */

function ChosenSlot({ choice, onChange }: { choice: Choice; onChange: () => void }): ReactNode {
  const { site, clock } = useSite();
  return (
    <div className="chosen">
      <div>
        <strong>{choice.service.name}</strong>
        <p className="chosen__when">{clock.slot(choice.slot.start, choice.slot.end)}</p>
        <p className="chosen__price">
          {money(choice.service.price_cents, site.currency)} ·{' '}
          {duration(choice.service.duration_min)}
        </p>
      </div>
      <button className="button button--quiet" type="button" onClick={onChange}>
        Change
      </button>
    </div>
  );
}

function Confirm({
  choice,
  intentId,
  onBooked,
  onChange,
  onSessionEnded,
}: {
  choice: Choice;
  /** Minted once when this step is entered, so two taps are one booking. */
  intentId: string;
  onBooked: (booking: BookingRecord) => void;
  onChange: () => void;
  onSessionEnded: () => void;
}): ReactNode {
  const { api, guest, expire } = useSite();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const booking = await api.createBooking({
        service_id: choice.service.service_id,
        // The resource the availability answer offered, echoed back. The SPA
        // never invents one, and never shows one: which room a guest is in is
        // not a choice this surface offers (`routes/site.ts`).
        resource_id: choice.slot.resource_id,
        start: choice.slot.start,
        party_size: 1,
        client_intent_id: intentId,
        // There is no price here. There is nowhere to put one.
      });
      onBooked(booking);
    } catch (e) {
      const failure = asFailure(e);
      if (failure.code === 'unauthenticated') {
        expire();
        onSessionEnded();
        return;
      }
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const failure = error === null ? null : asFailure(error);

  return (
    <div className="flow">
      <ChosenSlot choice={choice} onChange={onChange} />

      {guest === null ? null : <p className="flow__who">Booking for {guest.displayName}.</p>}

      {failure === null ? null : <Failure error={failure} />}
      {failure?.code === 'conflict' ? (
        // The exclusion constraint fired: somebody took the time while this
        // guest was reading it. The honest move is back to the picker with the
        // day still selected, not a retry that will lose the race again.
        <button className="button button--primary" type="button" onClick={onChange}>
          Choose another time
        </button>
      ) : (
        <button
          className="button button--primary"
          type="button"
          disabled={busy}
          onClick={() => void confirm()}
        >
          {busy ? 'Booking…' : 'Confirm booking'}
        </button>
      )}
    </div>
  );
}

/**
 * One id per confirm attempt, used as both `client_intent_id` and
 * `Idempotency-Key` (`readIntent` accepts either).
 *
 * It is minted when the confirm step is *entered*, not when the button is
 * pressed, which is the whole point: a guest who taps "Confirm" twice on a bad
 * connection sends the same key twice and `guest-api` replays the first answer
 * byte for byte instead of booking the room again.
 */
function intentId(): string {
  const random = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof random?.randomUUID === 'function') return random.randomUUID();
  return `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
